/**
 * base64url, both directions, without a global.
 *
 * RFC 4648 §5 — the alphabet with `-` and `_` instead of `+` and `/`, and no padding. It is what
 * a JWT is written in, what a PKCE challenge is written in, and what every token this product
 * hands to a browser is written in, so it turns up in three packages at once.
 *
 * Written by hand rather than over `btoa`/`atob`, for three reasons that are each enough on
 * their own. Those two are Web globals: they exist in a Worker and a browser and are absent from
 * some of the runtimes `shared-types` promises to be importable from. They speak *binary
 * strings*, so every use is bracketed by a conversion that is easy to get subtly wrong on
 * anything above U+00FF. And `atob` throws on bad input, where a decoder at a trust boundary
 * wants to answer `null` — the string being decoded arrived in a cookie or a header, and a
 * malformed one is a request to refuse, not an exception to catch three frames up.
 *
 * `null` rather than a `Result` here on purpose: this is a primitive, and "that is not base64url"
 * needs no code, no message and no retry advice. The caller that cares turns it into an error
 * with the context only it has.
 */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/**
 * The reverse table, built once.
 *
 * 128 entries covering the whole of ASCII, `-1` meaning "not in the alphabet". A lookup rather
 * than an `indexOf` per character: decoding a JWKS is thousands of characters and this is on the
 * path of every request that carries a token.
 */
const VALUES = (() => {
    const table = new Int8Array(128).fill(-1);
    for (let index = 0; index < ALPHABET.length; index += 1) {
        table[ALPHABET.charCodeAt(index)] = index;
    }
    return table;
})();
export function toBase64Url(bytes) {
    let out = '';
    const chunks = [];
    for (let index = 0; index < bytes.length; index += 3) {
        // `?? 0` is the tail: the last group may be one or two bytes, and the bits that were never
        // there are dropped below rather than encoded as zeroes.
        const first = bytes[index] ?? 0;
        const second = bytes[index + 1] ?? 0;
        const third = bytes[index + 2] ?? 0;
        const triple = (first << 16) | (second << 8) | third;
        const remaining = bytes.length - index;
        // `charAt` rather than `[]`: the index is masked to six bits and cannot miss, but under
        // `noUncheckedIndexedAccess` a subscript is `string | undefined` and the honest way to say
        // "this alphabet has all 64 entries" is the accessor that returns a `string` either way.
        out += ALPHABET.charAt((triple >> 18) & 63);
        out += ALPHABET.charAt((triple >> 12) & 63);
        if (remaining > 1)
            out += ALPHABET.charAt((triple >> 6) & 63);
        if (remaining > 2)
            out += ALPHABET.charAt(triple & 63);
        if (out.length >= 16384) {
            chunks.push(out);
            out = '';
        }
    }
    chunks.push(out);
    return chunks.join('');
}
/** `null` when the text is not base64url, or is a length no encoding could have produced. */
export function fromBase64Url(text) {
    // A group of one character is impossible: six bits cannot encode a byte. Rejecting it here
    // means the loop below never has to decide what half a byte means.
    if (text.length % 4 === 1)
        return null;
    const bytes = new Uint8Array(Math.floor((text.length * 3) / 4));
    let written = 0;
    let accumulator = 0;
    let bits = 0;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        const value = code < 128 ? (VALUES[code] ?? -1) : -1;
        // Padding included: `=` is legal base64 and is not part of *this* encoding, so a string
        // carrying it was produced by something else and is refused rather than trimmed.
        if (value < 0)
            return null;
        accumulator = (accumulator << 6) | value;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            bytes[written] = (accumulator >> bits) & 0xff;
            written += 1;
        }
    }
    // The leftover bits of a partial group must be zero. A decoder that ignores them accepts
    // several encodings of the same bytes, which is how a token that was rejected once gets
    // accepted on the next spelling.
    if (bits > 0 && (accumulator & ((1 << bits) - 1)) !== 0)
        return null;
    return bytes.subarray(0, written);
}
const encoder = new TextEncoder();
export function utf8ToBase64Url(text) {
    return toBase64Url(encoder.encode(text));
}
/** `null` when the text is not base64url or the bytes are not UTF-8. */
export function base64UrlToUtf8(text) {
    const bytes = fromBase64Url(text);
    if (bytes === null)
        return null;
    try {
        // Fatal, because the alternative is `�` — a replacement character silently changing a
        // token's payload into a different, valid-looking one.
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=base64.js.map